import {
  useState,
  useEffect,
  useRef,
  useCallback,
  useContext,
  type MutableRefObject
} from 'react'
import mapboxgl from 'mapbox-gl'
import _isUndefined from 'lodash/isUndefined'
import _isNil from 'lodash/isNil'
import { type LngLatBoundsLike, type Map as MapBox } from 'mapbox-gl'
import cn from 'clsx'

import useClustersLayer from '../../pages/insights/map/hooks/useClustersLayer'
import useHandleClusterClick from '../../pages/insights/map/hooks/useHandleClusterClick'
import usePolygonsLayer from '../../pages/insights/map/hooks/usePolygonsLayer'
import useHandlePolygonClick from '../../pages/insights/map/hooks/useHandlePolygonClick'
import useCenterPolygons from '../../pages/insights/map/hooks/useCenterPolygons'
import useSelectedEmissionsMarkers from '../../pages/insights/map/hooks/useSelectedEmissionsMarkers'
import useHandlePolygonHover from '../../pages/insights/map/hooks/useHandlePolygonHover'
import genGeoJSON from '../../../src/utils/data/gen_geo_json'
import { drawEstateBoundariesFromGeoJSON } from './drawEstateGeoJSON'
import { type GeoJSON, type Emissions } from '../../types/data'
import handleFeatureClick from '../../pages/insights/map/utils/handleFeatureClick'
import { type IGBPLandCoverClass } from 'types'
import { FEATURES_SOURCE_ID } from '../../pages/insights/map/const'
import MapviewButton from './MapviewButton'
import DataAttributionTooltip from '../DataAttribution/DataAttributionTooltip'

import 'mapbox-gl/dist/mapbox-gl.css'

import UIContext from 'ui_context'

import * as H from '../../hooks'
import { CLASS_NAME } from './const'

import './style.scss'

const { REACT_APP_MAPBOX_ACCESS_TOKEN } = process.env

if (!_isUndefined(REACT_APP_MAPBOX_ACCESS_TOKEN)) {
  mapboxgl.accessToken = REACT_APP_MAPBOX_ACCESS_TOKEN
}

export enum MapView {
  DEFAULT = 'default',
  SATELLITE = 'satellite'
}

type Props = {
  className?: string
  filteredEmissions: Emissions[]
  selectedEmissions: Emissions[]
  selectedPolygonUUIDs: string[]
  setSelectedEmissions: (selectedEmissions: Emissions[]) => void
}

export default function Map({
  className,
  filteredEmissions,
  selectedEmissions,
  selectedPolygonUUIDs,
  setSelectedEmissions
}: Props) {
  const { sub } = useContext(UIContext)
  const mapContainerRef = useRef<HTMLDivElement>(null)
  const [initializedMap, setInitializedMap] = useState<boolean>(false)

  const mapRef = useRef<MapBox>()
  const [stateMapRef, setStateMapRef] =
    useState<MutableRefObject<MapBox | undefined>>(mapRef)
  const [mapView, setMapView] = useState<MapView>(MapView.DEFAULT)
  const finalClassName = H.useClassName(CLASS_NAME, className as string)
  const geoJsonWithPolygonGeometry = H.useFunctionMemo<GeoJSON>(
    genGeoJSON,
    filteredEmissions,
    true
  )
  const geoJsonWithPointGeometry = H.useFunctionMemo<GeoJSON>(
    genGeoJSON,
    filteredEmissions
  )
  const mapStyle =
    mapView === MapView.DEFAULT
      ? 'mapbox://styles/mapbox/light-v10'
      : 'mapbox://styles/mapbox/satellite-v9'

  useEffect((): (() => void) | undefined => {
    if (initializedMap || mapContainerRef.current === null) {
      return
    }

    setInitializedMap(true)

    const map = new mapboxgl.Map({
      style: mapStyle,
      container: mapContainerRef.current as HTMLElement
    })

    map.addControl(new mapboxgl.NavigationControl())

    map.on('style.load', (): void => {
      setStateMapRef({ current: map })
      stateMapRef.current = map
      void drawEstateBoundariesFromGeoJSON(map, sub)
    })

    return (): void => {
      try {
        stateMapRef?.current?.remove()
      } catch (error) {
        console.error(error)
      }
    }
  }, [mapContainerRef.current, initializedMap, sub])

  useEffect(() => {
    stateMapRef.current?.setStyle(mapStyle)
  }, [mapView])

  useEffect(() => {
    const applyFeatureState = () => {
      filteredEmissions.forEach((polygon) => {
        const isEmission = polygon.total > 0

        map?.setFeatureState(
          {
            id: polygon.id,
            source: FEATURES_SOURCE_ID
          },
          { isEmission }
        )
      })
    }

    const map = stateMapRef.current

    if (map != null) {
      map.on('styledata', applyFeatureState)
    }

    return () => {
      if (map != null) {
        map.off('styledata', applyFeatureState)
      }
    }
  }, [filteredEmissions, stateMapRef])

  const handleMarkerClick = useCallback(
    (emission: Emissions) => {
      if (
        _isNil(stateMapRef.current) ||
        _isUndefined(stateMapRef.current) ||
        _isUndefined((stateMapRef.current as any).style)
      ) {
        return
      }

      const {
        feature: {
          geometry: { coordinates }
        }
      } = emission
      const bounds = [coordinates, coordinates] as unknown as LngLatBoundsLike

      if (stateMapRef.current !== null) {
        stateMapRef.current.fitBounds(bounds, {
          padding: 100,
          maxZoom: 13,
          duration: 2000
        })
      }
    },
    [stateMapRef.current]
  )

  const onFeatureClick = useCallback(
    (featureUUID: string, featureLCC: IGBPLandCoverClass) => {
      handleFeatureClick(
        filteredEmissions,
        selectedEmissions,
        setSelectedEmissions,
        featureUUID,
        featureLCC
      )
    },
    [filteredEmissions, selectedEmissions, setSelectedEmissions]
  )

  useCenterPolygons(
    stateMapRef.current,
    selectedEmissions,
    filteredEmissions,
    handleMarkerClick
  )
  usePolygonsLayer(stateMapRef.current, geoJsonWithPolygonGeometry)
  useClustersLayer(
    stateMapRef.current,
    geoJsonWithPointGeometry,
    selectedEmissions
  )
  useSelectedEmissionsMarkers(
    stateMapRef.current,
    selectedEmissions,
    filteredEmissions,
    handleMarkerClick
  )
  useHandleClusterClick(stateMapRef.current)
  useHandlePolygonClick(
    onFeatureClick,
    stateMapRef.current,
    selectedPolygonUUIDs
  )
  useHandlePolygonHover(stateMapRef.current, filteredEmissions)

  return (
    <>
      <div className={finalClassName} ref={mapContainerRef} />
      <div
        className={cn(`${CLASS_NAME}-data-attribution-wrapper`, {
          [`${CLASS_NAME}-data-attribution-light`]:
            mapView === MapView.SATELLITE
        })}
      >
        <DataAttributionTooltip />
      </div>
      <MapviewButton mapView={mapView} setMapView={setMapView} />
    </>
  )
}

export { CLASS_NAME }
